import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { Panel } from "@/components/ui/panel";
import { MySkillsPicker } from "@/components/profile/my-skills-picker";
import { SettingsPageShell } from "@/components/shared/settings-page-shell";
import { useMySkillIds, useSkillBank } from "@/hooks/use-skills";
import { useStore } from "@/stores/store-context";

export const ProfileSkillsPage = observer(function ProfileSkillsPage() {
  const { t } = useTranslation();
  const { router, workspace } = useStore();
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  // The workspace's live skills and the member's own, as cached reads (NG-2802).
  const bank = useSkillBank(activeSlug ?? undefined);
  const mine = useMySkillIds(activeSlug ?? undefined);
  const skills = useMemo(() => bank.data.filter((skill) => !skill.archived_at), [bank.data]);
  const readFailed = Boolean(bank.error ?? mine.error);
  useEffect(() => {
    // The query layer logs the failure; this tells the user.
    if (readFailed) toast.error(t("profile.skills.load_error"));
    // Once per failure: keyed on the failure alone, not the translator.
  }, [readFailed]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        if (workspace.workspaces.length === 0) {
          await workspace.fetchWorkspaces();
        }
        if (cancelled) return;

        const slugFromRoute = router.workspaceSlug;
        const slug =
          (slugFromRoute && workspace.workspaces.find((ws) => ws.slug === slugFromRoute)?.slug) ||
          workspace.workspaces[0]?.slug ||
          null;

        if (!slug) {
          setActiveSlug(null);
          return;
        }

        if (cancelled) return;

        setActiveSlug(slug);
      } catch (error) {
        console.error("[profile-skills] workspaces failed to load", { error });
        if (!cancelled) toast.error(t("profile.skills.load_error"));
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [router.workspaceSlug, workspace]);

  return (
    <SettingsPageShell
      title={t("profile.skills.title")}
      description={t("profile.skills.description")}
    >
      {!activeSlug ? (
        <Panel>
          <p className="text-sm text-muted-foreground">{t("profile.skills.no_workspace")}</p>
        </Panel>
      ) : (
        <MySkillsPicker workspaceSlug={activeSlug} skills={skills} />
      )}
    </SettingsPageShell>
  );
});
